import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSubredditFromUrl, isSubdomainPattern, displayImageSourceForCfbFlairWizard } from '../utils/subreddit';
import {
  Container,
  Typography,
  Box,
  Paper,
  AppBar,
  Toolbar,
  IconButton,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import type { TooltipProps } from 'recharts';
import { CDN_BASE_URL, CDN_IMAGE_PATH } from '../config';
import { formatTrendTickFromMs, parseSqlDateStringLocal } from '../utils/sqlCalendarDate';

// Color palette for chart lines
const colors = [
  '#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00ff00',
  '#0088fe', '#00c49f', '#ffbb28', '#ff8042', '#8884d8'
];

interface TrendDataPoint {
  date: string;
  total: number;
  [key: string]: string | number; // Individual breakdown counts
}

interface BreakdownTrend {
  name: string;
  type: 'class' | 'family' | 'genus' | 'flair' | 'image';
  /** Stable id from master_sheet (matches filter_value / rollup dimension_key) */
  dimension_id?: string;
  flair_code?: string;
  image_id?: string;
  image_source?: string;
  data: Array<{ date: string; count: number }>;
}

interface FilterOption {
  id: string;
  name: string;
}

const TrendsPage: React.FC = () => {
  const { subreddit: rawSubreddit } = useParams<{ subreddit: string }>();
  const navigate = useNavigate();
  const fromUrl = getSubredditFromUrl();
  const subreddit = (rawSubreddit?.toLowerCase() || fromUrl) || '';
  const canonicalSubredditName = rawSubreddit || fromUrl || '';

  const [trendData, setTrendData] = useState<TrendDataPoint[]>([]);
  const [breakdownTrends, setBreakdownTrends] = useState<BreakdownTrend[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterLevel, setFilterLevel] = useState<'all' | 'class' | 'family' | 'genus' | 'flair'>('all');
  const [filterValue, setFilterValue] = useState<string>('');
  const [filterOptions, setFilterOptions] = useState<FilterOption[]>([]);
  /** When set (e.g. drill from class line), family dropdown = families in this class only. */
  const [parentContext, setParentContext] = useState<{ level: string; id: string } | null>(null);
  const [timeBucket, setTimeBucket] = useState<'30days' | '1year' | 'all'>('30days');
  /** Server hint (timeouts, unsupported combos, deploy steps) */
  const [metaMessage, setMetaMessage] = useState<string | null>(null);

  /** Filter dropdown options: fast DISTINCT query only — not blocked by chart SQL. */
  useEffect(() => {
    if (!subreddit) return;
    let cancelled = false;
    (async () => {
      try {
        const { API_BASE_URL } = await import('../config');
        const params = new URLSearchParams();
        params.append('filter_level', filterLevel);
        if (parentContext) {
          params.append('parent_level', parentContext.level);
          params.append('parent_value', parentContext.id);
        }
        const response = await fetch(
          `${API_BASE_URL}/trends/${subreddit}/filter-options?${params.toString()}`
        );
        if (!response.ok || cancelled) return;
        const data = await response.json();
        if (!cancelled) {
          const raw = data.filter_options || [];
          const normalized: FilterOption[] = Array.isArray(raw)
            ? raw.map((o: FilterOption | string) =>
                typeof o === 'string' ? { id: o, name: o } : { id: String(o.id), name: o.name }
              )
            : [];
          setFilterOptions(normalized);
        }
      } catch {
        /* non-fatal */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [subreddit, filterLevel]);

  useEffect(() => {
    if (subreddit) {
      loadTrendData();
    }
  }, [subreddit, filterLevel, filterValue, timeBucket, parentContext]);

  const loadTrendData = async () => {
    setIsLoading(true);
    setMetaMessage(null);
    try {
      const { API_BASE_URL } = await import('../config');
      const params = new URLSearchParams();
      // Always send filter_level, even if filterValue is empty
      params.append('filter_level', filterLevel);
      if (filterValue) {
        params.append('filter_value', filterValue);
      }
      if (parentContext) {
        params.append('parent_level', parentContext.level);
        params.append('parent_value', parentContext.id);
      }
      params.append('time_bucket', timeBucket);
      console.log('[TrendsPage] Loading trend data with params:', {
        filterLevel,
        filterValue,
        timeBucket,
        url: `${API_BASE_URL}/trends/${subreddit}?${params.toString()}`
      });
      const response = await fetch(`${API_BASE_URL}/trends/${subreddit}?${params.toString()}`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('[TrendsPage] Received data:', {
          trend_data_length: data.trend_data?.length || 0,
          breakdown_trends_length: data.breakdown_trends?.length || 0,
          trend_data: data.trend_data,
          breakdown_trends: data.breakdown_trends
        });
        setTrendData(data.trend_data || []);
        setBreakdownTrends(data.breakdown_trends || []);
        setMetaMessage(typeof data.meta?.message === 'string' ? data.meta.message : null);
        setError(null);
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Failed to load trend data' }));
        const errorMsg = errorData.error || errorData.details || `HTTP ${response.status}`;
        setError(errorMsg);
        console.error('Error loading trend data:', errorData);
      }
    } catch (error: any) {
      const errorMsg = error.message || 'Failed to load trend data';
      setError(errorMsg);
      console.error('Error loading trend data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Prepare chart data and compute y-axis max (with 5% padding)
  const { chartData, yAxisMax } = useMemo(() => {
    if (!trendData.length) return { chartData: [], yAxisMax: 'auto' as const };
    
    let maxVal = 0;
    const data = trendData.map(point => {
      // DB date is a calendar day; avoid new Date('YYYY-MM-DD') (UTC midnight → wrong local day)
      const dateObj = parseSqlDateStringLocal(point.date);
      const dateTimestamp = dateObj.getTime();
      const result: Record<string, string | number> = {
        date: dateTimestamp,
        dateLabel: dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        total: point.total,
      };
      maxVal = Math.max(maxVal, point.total || 0);

      // Add breakdown lines (limit to top 15 for readability)
      breakdownTrends.slice(0, 15).forEach(breakdown => {
        const breakdownPoint = breakdown.data.find(d => d.date === point.date);
        const key = breakdown.name.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
        const count = breakdownPoint ? breakdownPoint.count : 0;
        result[key] = count;
        maxVal = Math.max(maxVal, count);
      });

      return result;
    });
    const yMax = maxVal > 0 ? Math.ceil(maxVal * 1.05) : 'auto';
    return { chartData: data, yAxisMax: yMax };
  }, [trendData, breakdownTrends]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="static" color="primary" sx={{ flexShrink: 0 }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate(isSubdomainPattern() ? '/' : `/${canonicalSubredditName}`)}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <TrendingUpIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            /r/{canonicalSubredditName} Flair Trends
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4, flex: 1 }}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            Flair Trends Over Time
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            View the rate of change for flairs over time. Filter by class, family, genus, or individual flair.
          </Typography>

          {/* Filters */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Time Period</InputLabel>
              <Select
                value={timeBucket}
                onChange={(e) => setTimeBucket(e.target.value as '30days' | '1year' | 'all')}
                label="Time Period"
              >
                <MenuItem value="30days">30 Days</MenuItem>
                <MenuItem value="1year">1 Year</MenuItem>
                <MenuItem value="all">All Time</MenuItem>
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Filter Level</InputLabel>
              <Select
                value={filterLevel}
                onChange={(e) => {
                  setFilterLevel(e.target.value as any);
                  setFilterValue('');
                  setParentContext(null);
                }}
                label="Filter Level"
              >
                <MenuItem value="all">All Flairs</MenuItem>
                <MenuItem value="class">By Class</MenuItem>
                <MenuItem value="family">By Family</MenuItem>
                <MenuItem value="genus">By Genus</MenuItem>
                <MenuItem value="flair">By Flair</MenuItem>
              </Select>
            </FormControl>

            {filterLevel !== 'all' && (
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Select {filterLevel.charAt(0).toUpperCase() + filterLevel.slice(1)}</InputLabel>
                <Select
                  value={filterValue}
                  onChange={(e) => setFilterValue(e.target.value)}
                  label={`Select ${filterLevel.charAt(0).toUpperCase() + filterLevel.slice(1)}`}
                >
                  <MenuItem value="">All</MenuItem>
                  {filterOptions.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">
              Error loading trend data: {error}
            </Alert>
          ) : trendData.length === 0 && breakdownTrends.length === 0 ? (
            metaMessage ? (
              <Alert severity="warning">{metaMessage}</Alert>
            ) : (
              <Alert severity="info">
                No trend data available yet. Flair history is tracked daily, so data will appear after the nightly job runs. If you have flair history data, try selecting a different time period or filter.
              </Alert>
            )
          ) : (
            <Box>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {filterLevel === 'all' && 'Total Users & Breakdown by Class'}
                  {filterLevel === 'genus' && filterValue && `Total Users in ${filterValue} & Breakdown by Flair`}
                  {filterLevel === 'genus' && !filterValue && 'Total Users & Breakdown by Genus'}
                  {filterLevel === 'flair' && filterValue && `Total Users with ${filterValue} & Breakdown by Image`}
                  {filterLevel === 'flair' && !filterValue && 'Total Users & Breakdown by Flair'}
                  {filterLevel === 'class' && 'Total Users & Breakdown by Class'}
                  {filterLevel === 'family' && 'Total Users & Breakdown by Family'}
                  {timeBucket === '30days' && ' (Last 30 Days)'}
                  {timeBucket === '1year' && ' (Last Year)'}
                  {timeBucket === 'all' && ' (All Time)'}
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      type="number"
                      scale="time"
                      domain={['dataMin', 'dataMax']}
                      angle={-90}
                      textAnchor="end"
                      height={timeBucket === 'all' ? 120 : 80}
                      dy={timeBucket === 'all' ? 25 : 20}
                      tick={{ fontSize: 11 }}
                      tickFormatter={(value: unknown) => {
                        if (value == null) return '';
                        const n = typeof value === 'number' ? value : Number(value);
                        if (Number.isNaN(n)) return '';
                        return formatTrendTickFromMs(n, timeBucket);
                      }}
                    />
                    <YAxis domain={[0, yAxisMax]} />
                    <Tooltip 
                      content={({ active, payload }: TooltipProps<number, string>) => {
                        if (!active || !payload || payload.length === 0) return null;
                        
                        // Sort payload by value (descending), excluding "total" and date-related keys
                        const sortedPayload = [...payload]
                          .filter((entry: { dataKey?: string | number }) => {
                            const key = entry.dataKey as string;
                            return key !== 'total' && key !== 'date' && key !== 'dateLabel';
                          })
                          .map((entry: { dataKey?: string | number; value?: number | string }) => {
                            // Ensure we have a numeric value
                            let numValue = 0;
                            if (typeof entry.value === 'number') {
                              numValue = entry.value;
                            } else if (typeof entry.value === 'string') {
                              numValue = parseFloat(entry.value) || 0;
                            }
                            return { ...entry, numValue };
                          })
                          .sort((a, b) => {
                            return b.numValue - a.numValue; // Descending order
                          });
                        
                        // Add "total" at the top if it exists
                        const totalEntry = payload.find((entry: { dataKey?: string | number }) => entry.dataKey === 'total');
                        const finalPayload = totalEntry 
                          ? [totalEntry, ...sortedPayload]
                          : sortedPayload;
                        
                        // Find the breakdown for the first non-total entry (for image display)
                        let breakdown = null;
                        for (const entry of sortedPayload) {
                          breakdown = breakdownTrends.find(b => {
                            const key = b.name.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
                            return key === entry.dataKey;
                          });
                          if (breakdown) break;
                        }
                        
                        const imageUrl = breakdown?.image_source 
                          ? `${CDN_BASE_URL}/${CDN_IMAGE_PATH}/${subreddit}/${displayImageSourceForCfbFlairWizard(breakdown.image_source)}.png`
                          : breakdown?.flair_code
                          ? `${CDN_BASE_URL}/${CDN_IMAGE_PATH}/${subreddit}/${breakdown.flair_code}.png`
                          : null;
                        
                        return (
                          <Box sx={{ 
                            bgcolor: 'background.paper', 
                            border: '1px solid', 
                            borderColor: 'divider',
                            borderRadius: 1,
                            p: 1.5,
                            boxShadow: 2
                          }}>
                            {imageUrl && (
                              <Box sx={{ mb: 1, textAlign: 'center' }}>
                                <img 
                                  src={imageUrl} 
                                  alt={breakdown?.name || ''}
                                  style={{ 
                                    width: '60px', 
                                    height: '60px',
                                    objectFit: 'contain'
                                  }}
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              </Box>
                            )}
                            {finalPayload.map((entry: { color?: string; name?: string; value?: number | string }, index: number) => (
                              <Typography key={index} variant="body2" sx={{ color: entry.color }}>
                                {entry.name}: {entry.value}
                              </Typography>
                            ))}
                          </Box>
                        );
                      }}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="total" 
                      stroke="#8884d8" 
                      strokeWidth={2}
                      name="Total Users"
                      dot={{ r: 4 }}
                    />
                    {breakdownTrends.slice(0, 15).map((breakdown, index) => {
                      const key = breakdown.name.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
                      
                      // Determine next filter level based on current level and breakdown type
                      // Hierarchy: all -> class -> family -> genus -> flair
                      const dim = breakdown.dimension_id ?? '';
                      const applyDrill = () => {
                        if (filterLevel === 'all' && breakdown.type === 'class') {
                          setFilterLevel('family');
                          setParentContext({ level: 'class', id: dim });
                          setFilterValue('');
                          return;
                        }
                        if (filterLevel === 'class' && breakdown.type === 'class') {
                          setFilterLevel('class');
                          setParentContext(null);
                          setFilterValue(dim);
                          return;
                        }
                        if (filterLevel === 'class' && breakdown.type === 'family') {
                          setFilterLevel('family');
                          setParentContext(null);
                          setFilterValue(dim);
                          return;
                        }
                        if (filterLevel === 'family' && breakdown.type === 'family') {
                          setFilterLevel('family');
                          setParentContext(null);
                          setFilterValue(dim);
                          return;
                        }
                        if (filterLevel === 'family' && breakdown.type === 'genus') {
                          setFilterLevel('genus');
                          setParentContext(null);
                          setFilterValue(dim);
                          return;
                        }
                        if (filterLevel === 'genus' && breakdown.type === 'genus') {
                          setFilterLevel('genus');
                          setParentContext(null);
                          setFilterValue(dim);
                          return;
                        }
                        if (filterLevel === 'genus' && breakdown.type === 'flair') {
                          setFilterLevel('flair');
                          setParentContext(null);
                          setFilterValue(dim);
                          return;
                        }
                        if (filterLevel === 'flair' && breakdown.type === 'flair') {
                          setFilterLevel('flair');
                          setParentContext(null);
                          setFilterValue(dim);
                          return;
                        }
                      };

                      const isClickable =
                        (filterLevel === 'all' && breakdown.type === 'class') ||
                        (filterLevel === 'class' && ['class', 'family'].includes(breakdown.type)) ||
                        (filterLevel === 'family' && ['family', 'genus'].includes(breakdown.type)) ||
                        (filterLevel === 'genus' && ['genus', 'flair'].includes(breakdown.type)) ||
                        (filterLevel === 'flair' && breakdown.type === 'flair');
                      
                      return (
                        <Line
                          key={`${breakdown.type}-${breakdown.name}-${index}`}
                          type="monotone"
                          dataKey={key}
                          stroke={colors[index % colors.length]}
                          strokeWidth={1.5}
                          name={breakdown.name}
                          dot={{ 
                            r: 3,
                            ...(isClickable ? {
                              onClick: () => applyDrill(),
                              style: { cursor: 'pointer' }
                            } : {})
                          }}
                          activeDot={{
                            r: 6,
                            ...(isClickable ? {
                              onClick: () => applyDrill(),
                              style: { cursor: 'pointer' }
                            } : {})
                          }}
                        />
                      );
                    })}
                  </LineChart>
                </ResponsiveContainer>
              </Paper>

              {breakdownTrends.length > 15 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Showing top 15 items. Total: {breakdownTrends.length} items tracked.
                </Alert>
              )}
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default TrendsPage;
